import cn from 'classnames';
import { API_KEYS_COLUMNS, COMMON_ERROR_TEXT, SVG_SIZE_M } from 'src/constants';
import dayjs from 'dayjs';
import { Trash } from '@phosphor-icons/react';
import styles from './ApiKeysTable.module.scss';
import { Button } from 'src/v2/commonComponents/Button';
import { toast } from 'react-toastify';
import { SpinnerSimple } from 'src/v2/commonComponents/SpinnerSimple';
import {
  useDeleteApiKeyMutation,
  useGetApiKeysListQuery,
} from 'src/store/services';
import { useSession, useVisible } from 'src/hooks';
import { CommonDeleteModal } from 'src/components/CommonDeleteModal';
import { useState } from 'react';
import { CopyAPIKeyField } from '../CopyAPIKeyField';

export const ApiKeysTable = () => {
  const {
    appUser: { user_id },
    isOpenTier,
  } = useSession();

  const { data, isLoading } = useGetApiKeysListQuery({ user_id });
  const { api_keys: apiKeys } = data || {};

  const [apiKeyForDelete, setApiKeyForDelete] = useState<{
    apiKey: string;
    name: string;
    prefix: string;
  } | null>(null);

  const {
    isVisible: isDeleteModalVisible,
    onVisibilitySet: onDeleteModalVisibilitySet,
    onVisibilityRemove: onDeleteModalVisibilityRemove,
  } = useVisible();

  const [deleteApiKey, { isLoading: isDeleteApiKeyIsLoading }] =
    useDeleteApiKeyMutation();

  const onDeleteApiKey = async () => {
    if (!apiKeyForDelete) {
      toast.error('API key not found');
      return;
    }

    try {
      await deleteApiKey({ api_key: apiKeyForDelete.apiKey, user_id }).unwrap();
      toast.success('API key deleted');
      onDeleteModalVisibilityRemove();
    } catch (error) {
      toast.error(COMMON_ERROR_TEXT);
    }
  };

  const handleShowDeleteModal = (
    apiKey: string,
    name: string,
    prefix: string,
  ) => {
    setApiKeyForDelete({ apiKey, name, prefix });
    onDeleteModalVisibilitySet();
  };

  const getShortenKey = (key: string) => `${key}...`;

  return (
    <div className={styles.root}>
      <CommonDeleteModal
        isOpen={isDeleteModalVisible}
        onClose={onDeleteModalVisibilityRemove}
        onSubmit={onDeleteApiKey}
        isLoading={isDeleteApiKeyIsLoading}
        title="Delete API Key"
        cancelButtonTitle="Keep key active"
        deleteButtonTitle="Delete API key"
        customBody={
          <div className={styles.deleteModalBody}>
            <p>
              Are you sure you want to delete{' '}
              <span className={styles.keyName}>{apiKeyForDelete?.name}</span>.
              This API key will be permanently disabled and removed. All tasks
              using this key will fail, and this action cannot be undone or the
              key recovered.
            </p>

            <CopyAPIKeyField
              text={
                apiKeyForDelete?.prefix ? `${apiKeyForDelete?.prefix}...` : ''
              }
              withCopyButton={false}
            />
          </div>
        }
      />

      {(isLoading || !apiKeys?.length) && !isOpenTier && (
        <div className={styles.emptyState}>
          {isLoading ? <SpinnerSimple /> : 'Create a key to access Ninja API'}
        </div>
      )}

      {apiKeys && !!apiKeys.length && !isLoading && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.row}>
              {API_KEYS_COLUMNS.map((col) => (
                <th key={col.key} className={styles.headCell}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(apiKeys || []).map((apiKey, rowIndex) => {
              const isDeleteButtonDisabled =
                isDeleteApiKeyIsLoading &&
                apiKeyForDelete?.apiKey === apiKey.api_key;

              return (
                <tr key={rowIndex} className={styles.row}>
                  <td className={styles.cell}>{apiKey.name}</td>
                  <td className={styles.cell}>
                    {getShortenKey(apiKey.prefix)}
                  </td>
                  <td className={styles.cell}>
                    {dayjs(apiKey.created_at).format('MMM D, YYYY')}
                  </td>
                  <td className={cn(styles.cell, styles.actionsCell)}>
                    {apiKey.access_mode !== 'server_only' && (
                      <Button
                        color="transparent"
                        onClick={() =>
                          handleShowDeleteModal(
                            apiKey.api_key,
                            apiKey.name,
                            apiKey.prefix,
                          )
                        }
                        disabled={isDeleteButtonDisabled}
                      >
                        <Trash size={SVG_SIZE_M} />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
